import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import type { BannerResult } from '../../types/onboarding';
import signupOnboardingService from '../../services/api/signupOnboardingService';

interface SignupBannerGridStepProps {
  sessionId: string;
  onContinue: () => void;
  onSkip: () => void;
}

const TOTAL_COUNT = 10;
const POLL_INTERVAL_MS = 3000;
// gpt-image-2(参照画像あり)は1枚60〜80秒かかり、image_generation キューが
// 直列処理の環境では10枚で12分前後かかる。5分だと0枚のまま打ち切られるため20分。
const GENERATION_TIMEOUT_MS = 1200000; // 20 minutes

// --- Tailwind class constants ---
// keyframes は globals.css の sob-* / tailwind.config の taskyFadeInUp を参照。

const CONTAINER = 'text-center py-4 px-0 animate-fade-in-up';

const TITLE =
  'text-[1.5rem] font-bold text-gray-800 m-0 mb-2 max-[480px]:text-[1.25rem]';

const SUBTITLE = 'text-[0.9375rem] text-gray-500 m-0 mb-4';

const COUNTER_SECTION = 'mb-4';

const COUNTER_TEXT =
  'text-[1rem] font-semibold text-gray-700 mb-2 [&_span]:text-violet-600 [&_span]:text-[1.25rem]';

const PROGRESS_BAR_TRACK =
  'w-full max-w-[400px] h-2 bg-gray-100 rounded overflow-hidden mx-auto my-0';

const PROGRESS_BAR_FILL =
  'h-full rounded bg-gradient-to-r from-violet-600 to-violet-400 bg-[length:200%_100%] ' +
  'animate-[sob-shimmer_2s_ease-in-out_infinite] transition-[width] duration-500 ease-[ease]';

const GRID =
  'grid grid-cols-5 gap-3 mb-6 ' +
  'max-[1024px]:grid-cols-3 max-[1024px]:gap-2.5 ' +
  'max-[600px]:grid-cols-2 max-[600px]:gap-2';

// DownloadButton の `${BannerCard}:hover &` を group-hover で表現するため group を付与。
const BANNER_CARD_BASE =
  'group rounded-[10px] overflow-hidden transition-[transform,box-shadow] duration-200 ease-[ease] bg-white';
const BANNER_CARD_LOADED =
  'shadow-[0_4px_12px_rgba(0,0,0,0.1)] animate-[sob-scale-in_0.4s_ease-out] ' +
  'hover:-translate-y-1 hover:scale-[1.02] hover:shadow-[0_8px_24px_rgba(0,0,0,0.15)]';
const BANNER_CARD_IDLE = 'shadow-[0_2px_6px_rgba(0,0,0,0.05)]';

const IMAGE_WRAPPER = 'relative w-full aspect-square bg-gray-100 overflow-hidden';

const PLACEHOLDER_SLOT =
  'w-full h-full bg-[linear-gradient(90deg,#f3f4f6_25%,#e5e7eb_50%,#f3f4f6_75%)] bg-[length:200%_100%] ' +
  'animate-[sob-shimmer_1.5s_ease-in-out_infinite] flex items-center justify-center';

const PLACEHOLDER_NUMBER =
  'text-[1.25rem] font-bold text-gray-300 animate-[sob-dim-pulse_2s_ease-in-out_infinite]';

const BANNER_IMAGE =
  'w-full h-full object-cover block animate-[sob-scale-in_0.5s_ease-out]';

const STYLE_BADGE =
  'py-1.5 px-2 bg-white text-[0.6875rem] font-semibold text-gray-700 text-center whitespace-nowrap overflow-hidden text-ellipsis';

// right-* は派生（ExpandButton）で差し替えるため base には含めない。
const ICON_BUTTON_BASE =
  'absolute top-1.5 w-7 h-7 rounded-md border-none bg-white/90 text-gray-700 cursor-pointer ' +
  'flex items-center justify-center opacity-0 transition-[opacity,background-color] duration-200 ease-[ease] ' +
  'shadow-[0_1px_4px_rgba(0,0,0,0.1)] group-hover:opacity-100 hover:bg-violet-600 hover:text-white ' +
  '[&_svg]:w-[14px] [&_svg]:h-[14px]';
const DOWNLOAD_BUTTON = `${ICON_BUTTON_BASE} right-1.5`;
const EXPAND_BUTTON = `${ICON_BUTTON_BASE} right-10`;

const PREVIEW_OVERLAY =
  'fixed inset-0 z-[10000] grid place-items-center p-6 bg-[rgba(15,18,32,0.78)] backdrop-blur-[5px]';

const PREVIEW_PANEL =
  'relative w-[min(920px,100%)] max-h-[calc(100vh_-_48px)] pt-16 px-6 pb-6 rounded-2xl bg-white ' +
  'flex justify-center shadow-[0_30px_90px_rgba(0,0,0,0.35)] ' +
  '[&_img]:max-w-full [&_img]:max-h-[calc(100vh_-_136px)] [&_img]:object-contain';

const PREVIEW_ACTIONS = 'absolute top-[14px] right-4 flex gap-2';

const PREVIEW_ACTION =
  'min-h-[38px] py-0 px-[14px] border border-[#d8dbe6] rounded-lg bg-white text-[#303548] font-bold cursor-pointer';

const BUTTON_GROUP =
  'flex flex-col gap-3 items-center animate-[sob-fade-in-up_0.5s_ease-out_0.3s_both]';

const PRIMARY_BUTTON_BASE =
  'py-3.5 px-8 text-[1.0625rem] font-semibold text-white border-none rounded-lg ' +
  'transition-all duration-200 ease-[ease] ' +
  'enabled:hover:bg-violet-700 enabled:hover:-translate-y-px enabled:hover:shadow-[0_4px_12px_rgba(124,58,237,0.3)] ' +
  'enabled:active:translate-y-0 enabled:active:shadow-none';
const PRIMARY_BUTTON_ENABLED = 'bg-violet-600 cursor-pointer';
const PRIMARY_BUTTON_DISABLED = 'bg-violet-300 cursor-not-allowed';

const SKIP_BUTTON =
  'bg-transparent bg-none border-none text-gray-400 text-[0.8125rem] cursor-pointer py-2 px-4 ' +
  'transition-[color] duration-200 ease-[ease] hover:text-gray-500';

const ALL_FAILED_MESSAGE = 'text-[0.875rem] text-red-700 m-0 mb-4';

// --- Helpers ---

function downloadImage(url: string, filename: string) {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// --- Component ---

const SignupBannerGridStep = React.memo<SignupBannerGridStepProps>(
  ({ sessionId, onContinue, onSkip }) => {
    const [banners, setBanners] = useState<BannerResult[]>([]);
    const [completedCount, setCompletedCount] = useState(0);
    const [failedCount, setFailedCount] = useState(0);
    const [isPolling, setIsPolling] = useState(true);
    const [timedOut, setTimedOut] = useState(false);
    const [batchStatus, setBatchStatus] = useState<
      'processing' | 'completed' | 'failed'
    >('processing');
    const [previewBanner, setPreviewBanner] = useState<BannerResult | null>(
      null,
    );
    const mountedRef = useRef(true);
    const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
      return () => {
        mountedRef.current = false;
        if (pollTimerRef.current) {
          clearInterval(pollTimerRef.current);
        }
      };
    }, []);

    const poll = useCallback(async () => {
      if (!mountedRef.current || !sessionId) return;

      try {
        const statusData =
          await signupOnboardingService.getBannerStatus(sessionId);
        if (!mountedRef.current) return;

        setBanners(statusData.banners);
        setBatchStatus(statusData.status);

        let completed = 0;
        let failed = 0;
        statusData.banners.forEach((b) => {
          if (b.status === 'completed') completed += 1;
          if (b.status === 'failed') failed += 1;
        });
        setCompletedCount(completed);
        setFailedCount(failed);

        if (
          statusData.status === 'completed' ||
          statusData.status === 'failed'
        ) {
          setIsPolling(false);
          if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
          }
        }
      } catch {
        // Continue polling on transient errors
      }
    }, [sessionId]);

    useEffect(() => {
      if (!sessionId) return;

      poll();

      const startTime = Date.now();
      pollTimerRef.current = setInterval(() => {
        const elapsed = Date.now() - startTime;
        if (elapsed >= GENERATION_TIMEOUT_MS) {
          setIsPolling(false);
          setTimedOut(true);
          if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
          }
          return;
        }
        poll();
      }, POLL_INTERVAL_MS);

      return () => {
        if (pollTimerRef.current) {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
        }
      };
    }, [sessionId, poll]);

    const progressPercent = Math.round((completedCount / TOTAL_COUNT) * 100);
    // 1枚以上完成、またはポーリング終了（全滅含む）で次へ進める
    const canContinue = completedCount >= 1 || !isPolling;
    const allFailed =
      !isPolling &&
      completedCount === 0 &&
      (failedCount > 0 || batchStatus === 'failed' || timedOut);

    // バックエンドは2経路で banners を返す: キャッシュ経路(dispatch=bannerIndex順で安定)と、
    // キャッシュ消失時のDBフォールバック経路(rebuildStatusFromHistory=style_key昇順)。経路が
    // 切り替わると配列の並びが変わりうる。ここで style_key 昇順に正規化して並びを固定し、
    // ポーリング更新でサムネイルが同じマス目のまま別画像に入れ替わる（→クリックで意図しない
    // 画像が開く/DLされる）事故を防ぐ。style_key を持たない要素（フォールバックの未完成
    // プレースホルダ）は末尾へ回す。
    const orderedBanners = useMemo(() => {
      return banners
        .map((banner, index) => ({ banner, index }))
        .sort((a, b) => {
          const keyA = a.banner.style_key ?? '';
          const keyB = b.banner.style_key ?? '';
          if (keyA && keyB) {
            if (keyA !== keyB) return keyA < keyB ? -1 : 1;
            return a.index - b.index;
          }
          if (keyA) return -1;
          if (keyB) return 1;
          return a.index - b.index;
        })
        .map(({ banner }) => banner);
    }, [banners]);

    const slots = Array.from({ length: TOTAL_COUNT }, (_, i) => {
      const banner = orderedBanners[i] || null;
      const isCompleted = banner?.status === 'completed' && !!banner.image_url;
      // 完成済みは style_key を安定キーにして、並びが変わっても React が同じ画像タイルを
      // 追従させる（key=マス目番号だと並び替え時に src だけ差し替わり画像が化ける）。
      const cellKey =
        isCompleted && banner?.style_key
          ? `banner-${banner.style_key}`
          : `slot-${i}`;
      return { position: i, banner, isCompleted, cellKey };
    });

    const subtitle = (() => {
      if (isPolling) {
        return 'AIがあなたのサービスに最適なバナーを生成しています';
      }
      if (allFailed) {
        return 'バナーの生成に失敗しました。次へ進んでプラン選択へお進みください。';
      }
      if (timedOut) {
        return `生成に時間がかかっています（${completedCount}枚完成）。残りはバックグラウンドで生成が続き、完成したバナーは「バナー広告」ページに順次追加されます。`;
      }
      return '生成が完了しました';
    })();

    const stillGenerating = timedOut && completedCount < TOTAL_COUNT && !allFailed;

    return (
      <div className={`${CONTAINER} signup-banner-grid-step`}>
        <h2 className={TITLE}>
          {isPolling
            ? 'バナーを生成中...'
            : stillGenerating
              ? 'バナーを生成しています'
              : 'あなたのサービス専用のバナーが完成しました'}
        </h2>
        <p className={SUBTITLE}>{subtitle}</p>

        {allFailed && (
          <p className={ALL_FAILED_MESSAGE}>
            生成に失敗しましたが、そのまま次へ進めます。
          </p>
        )}

        <div className={COUNTER_SECTION}>
          <div className={COUNTER_TEXT}>
            生成済み:{' '}
            <span>
              {completedCount}/{TOTAL_COUNT}枚
            </span>
          </div>
          <div className={PROGRESS_BAR_TRACK}>
            <div
              className={PROGRESS_BAR_FILL}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        <div className={GRID}>
          {slots.map(({ position, banner, isCompleted, cellKey }) => (
            <div
              key={cellKey}
              className={`${BANNER_CARD_BASE} ${
                isCompleted ? BANNER_CARD_LOADED : BANNER_CARD_IDLE
              }`}
            >
              <div className={IMAGE_WRAPPER}>
                {isCompleted && banner?.image_url ? (
                  <>
                    <img
                      className={BANNER_IMAGE}
                      src={banner.image_url}
                      alt={`${banner.style_name} - ${position + 1}`}
                      loading="lazy"
                    />
                    <button
                      className={EXPAND_BUTTON}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        // クリック時点の画像を複製して固定する。以降のポーリング更新で
                        // banners 配列が入れ替わってもプレビューは差し替わらない。
                        setPreviewBanner({ ...banner });
                      }}
                      title="拡大表示"
                      aria-label="拡大表示"
                    >
                      <svg
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path d="M3 3h5v2H5v3H3V3Zm9 0h5v5h-2V5h-3V3ZM3 12h2v3h3v2H3v-5Zm12 0h2v5h-5v-2h3v-3Z" />
                      </svg>
                    </button>
                    <button
                      className={DOWNLOAD_BUTTON}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        // クリックしたそのマスの画像URLでDLする（並び替えの影響を受けない）。
                        downloadImage(
                          banner.image_url!,
                          `${banner.style_name || `banner-${position + 1}`}.png`,
                        );
                      }}
                      title="Download"
                    >
                      <svg
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </>
                ) : (
                  <div className={PLACEHOLDER_SLOT}>
                    <span className={PLACEHOLDER_NUMBER}>{position + 1}</span>
                  </div>
                )}
              </div>
              <div className={STYLE_BADGE}>
                {banner?.style_name || `Style ${position + 1}`}
              </div>
            </div>
          ))}
        </div>

        <div className={BUTTON_GROUP}>
          <button
            className={`${PRIMARY_BUTTON_BASE} ${
              canContinue ? PRIMARY_BUTTON_ENABLED : PRIMARY_BUTTON_DISABLED
            }`}
            type="button"
            onClick={onContinue}
            disabled={!canContinue}
          >
            次へ進む
          </button>
          <button className={SKIP_BUTTON} type="button" onClick={onSkip}>
            スキップ
          </button>
        </div>
        {previewBanner?.image_url && (
          <div
            className={PREVIEW_OVERLAY}
            onMouseDown={() => setPreviewBanner(null)}
          >
            <div
              className={PREVIEW_PANEL}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className={PREVIEW_ACTIONS}>
                <button
                  className={PREVIEW_ACTION}
                  type="button"
                  onClick={() =>
                    downloadImage(
                      previewBanner.image_url!,
                      `${previewBanner.style_name}.png`,
                    )
                  }
                >
                  ダウンロード
                </button>
                <button
                  className={PREVIEW_ACTION}
                  type="button"
                  onClick={() => setPreviewBanner(null)}
                >
                  閉じる
                </button>
              </div>
              <img
                src={previewBanner.image_url}
                alt={previewBanner.style_name}
              />
            </div>
          </div>
        )}
      </div>
    );
  },
);

SignupBannerGridStep.displayName = 'SignupBannerGridStep';

export default SignupBannerGridStep;
